import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const Panel = ({ src, alt, caption, index, tall, half, dim }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <motion.figure
      ref={ref}
      initial={{ opacity: 0, scale: 0.985 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
      className={`group relative overflow-hidden ${half ? "" : "w-full"} ${
        tall ? "h-[85vh] md:h-[95vh]" : "h-[60vh] md:h-[72vh]"
      }`}
      data-testid={`lookbook-panel-${index}`}
    >
      <motion.img
        src={src}
        alt={alt}
        style={{ y }}
        className={`absolute inset-0 h-[118%] w-full object-cover scale-105 transition-[filter] duration-700 group-hover:brightness-110 ${
          dim ? "[filter:brightness(0.82)_contrast(1.04)]" : ""
        }`}
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-ink/20" />
      <figcaption className="absolute bottom-8 left-6 md:left-12 flex items-baseline gap-4">
        <span className="text-xs uppercase tracking-[0.3em] text-gold">
          {String(index).padStart(2, "0")}
        </span>
        <span className="font-serif italic text-2xl md:text-4xl text-white">{caption}</span>
      </figcaption>
    </motion.figure>
  );
};

export const Lookbook = () => (
  <section id="lookbook" className="relative" data-testid="lookbook-section">
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 pt-8 pb-20 md:pb-28 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
      <motion.h2
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        className="font-serif font-light text-5xl md:text-7xl leading-none tracking-tight"
      >
        The <span className="italic gold-text-gradient">Lookbook</span>
      </motion.h2>
      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1, delay: 0.2 }}
        className="text-xs uppercase tracking-[0.3em] text-ash pb-2"
      >
        Worn, not carried
      </motion.p>
    </div>

    <Panel
      src="/assets/look-4.jpg"
      alt="RayStance Rosé — pink rimless frame with gold crosses, front view"
      caption="Rosé, face forward."
      index={1}
      tall
      dim
    />
    <div className="grid grid-cols-1 md:grid-cols-2">
      <Panel src="/assets/look-5.jpg" alt="RayStance Onyx — black frame, front view" caption="Onyx, eye to eye." index={2} half dim />
      <Panel src="/assets/look-3.jpg" alt="RayStance script logo on the Onyx temple" caption="Signed in script." index={3} half dim />
    </div>
    <Panel
      src="/assets/look-1.jpg"
      alt="RayStance clear rimless frame with gold cross accents"
      caption="Clarity, gilded."
      index={4}
      tall
      dim
    />
    <div className="grid grid-cols-1 md:grid-cols-2">
      <Panel src="/assets/look-2.jpg" alt="RayStance Reign — violet rimless frame, front view" caption="Reign, crowned." index={5} half dim />
      <Panel src="/assets/product-ember.webp" alt="RayStance Ember with amber lenses on concrete" caption="Ember, burning." index={6} half />
    </div>
  </section>
);
